/**
 * ClubList - List of running clubs
 */

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { cn } from '@/utils/cn';
import type { RunClub } from '@/types/running';

export function ClubList() {
  const clubs = useRunningStore((state) => state.clubs);
  const myClubs = useRunningStore((state) => state.myClubs);
  const fetchClubs = useRunningStore((state) => state.fetchClubs);
  const fetchMyClubs = useRunningStore((state) => state.fetchMyClubs);
  const joinClub = useRunningStore((state) => state.joinClub);
  const createClub = useRunningStore((state) => state.createClub);

  const [activeTab, setActiveTab] = useState<'discover' | 'my-clubs'>('discover');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    city: '',
    country: '',
    preferred_aqi_threshold: '',
  });

  useEffect(() => {
    const loadClubs = async () => {
      setIsLoading(true);
      await Promise.all([fetchClubs(), fetchMyClubs()]);
      setIsLoading(false);
    };
    loadClubs();
  }, [fetchClubs, fetchMyClubs]);

  const handleJoinClub = async (clubId: string) => {
    await joinClub(clubId);
  };

  const handleCreateClub = () => {
    if (!formData.name.trim() || !formData.city.trim() || !formData.country.trim()) {
      return;
    }

    createClub({
      name: formData.name.trim(),
      description: formData.description.trim(),
      city: formData.city.trim(),
      country: formData.country.trim(),
      preferred_aqi_threshold: formData.preferred_aqi_threshold ? parseInt(formData.preferred_aqi_threshold) : undefined,
    });

    // Reset form and close modal
    setFormData({ name: '', description: '', city: '', country: '', preferred_aqi_threshold: '' });
    setShowCreateModal(false);
    setActiveTab('my-clubs');
  };

  const displayClubs = activeTab === 'my-clubs' ? myClubs : clubs;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="px-4 flex gap-2">
        <button
          onClick={() => setActiveTab('discover')}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium transition-all',
            activeTab === 'discover'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800/50 text-text-secondary'
          )}
        >
          🔍 Discover
        </button>
        <button
          onClick={() => setActiveTab('my-clubs')}
          className={cn(
            'px-4 py-2 rounded-full text-sm font-medium transition-all',
            activeTab === 'my-clubs'
              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
              : 'bg-slate-800/50 text-text-secondary'
          )}
        >
          👥 My Clubs
        </button>
      </div>

      {/* Empty State */}
      {displayClubs.length === 0 && (
        <div className="text-center py-12 px-4">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-6xl mb-4"
          >
            👥
          </motion.div>
          <h3 className="text-xl font-bold text-white mb-2">
            {activeTab === 'my-clubs' ? 'No clubs joined yet' : 'No clubs found'}
          </h3>
          <p className="text-text-secondary">
            {activeTab === 'my-clubs'
              ? 'Join a club to connect with other runners!'
              : 'Be the first to create a running club!'}
          </p>
        </div>
      )}

      {/* Club List */}
      <div className="px-4 space-y-3">
        {displayClubs.map((club, index) => (
          <ClubCard
            key={club.id}
            club={club}
            index={index}
            onJoin={() => handleJoinClub(club.id)}
          />
        ))}
      </div>

      {/* Create Club Button */}
      <div className="px-4 pt-4">
        <button
          onClick={() => setShowCreateModal(true)}
          className={cn(
            'w-full py-4 rounded-xl',
            'bg-slate-800/50 border border-dashed border-glass-border',
            'text-text-secondary hover:text-white hover:border-emerald-500/30',
            'transition-all'
          )}
        >
          ➕ Create a Club
        </button>
      </div>

      {/* Create Club Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#0a0f14] border border-glass-border rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-4 border-b border-glass-border flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">Create a Club</h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-white"
                >
                  ✕
                </button>
              </div>

              {/* Form */}
              <div className="p-4 space-y-4">
                {/* Club Name */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Club Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Dublin Morning Runners"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Tell people about your club..."
                    rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:outline-none focus:border-emerald-500/50 resize-none"
                  />
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., Dublin"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>

                {/* Country */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="e.g., Ireland"
                    className="w-full px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:outline-none focus:border-emerald-500/50"
                  />
                </div>

                {/* AQI Threshold (Blurp unique) */}
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-1">
                    Max AQI Threshold (optional)
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={formData.preferred_aqi_threshold}
                      onChange={(e) => setFormData({ ...formData, preferred_aqi_threshold: e.target.value })}
                      placeholder="e.g., 50"
                      min="0"
                      max="500"
                      className="flex-1 px-4 py-3 rounded-xl bg-slate-800/50 border border-glass-border text-white placeholder:text-text-secondary focus:outline-none focus:border-emerald-500/50"
                    />
                    <div className="p-3 bg-cyan-500/10 border border-cyan-500/20 rounded-xl">
                      <span className="text-cyan-400 text-sm">☁️ AQI</span>
                    </div>
                  </div>
                  <p className="text-xs text-text-secondary mt-1">
                    Members will be encouraged to run only when AQI is below this level
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="p-4 border-t border-glass-border flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-3 rounded-xl bg-slate-800 text-white font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateClub}
                  disabled={!formData.name.trim() || !formData.city.trim() || !formData.country.trim()}
                  className={cn(
                    'flex-1 py-3 rounded-xl font-medium transition-all',
                    formData.name.trim() && formData.city.trim() && formData.country.trim()
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-700 text-text-secondary cursor-not-allowed'
                  )}
                >
                  Create Club
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

interface ClubCardProps {
  club: RunClub;
  index: number;
  onJoin: () => void;
}

function ClubCard({ club, index, onJoin }: ClubCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="bg-glass-bg backdrop-blur-xl border border-glass-border rounded-2xl overflow-hidden"
    >
      {/* Cover Image */}
      {club.cover_image_url ? (
        <div
          className="h-24 bg-cover bg-center"
          style={{ backgroundImage: `url(${club.cover_image_url})` }}
        />
      ) : (
        <div className="h-24 bg-gradient-to-r from-emerald-500/20 to-cyan-500/20" />
      )}

      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Logo */}
          {club.logo_url ? (
            <img
              src={club.logo_url}
              alt={club.name}
              className="w-12 h-12 rounded-xl object-cover -mt-8 border-2 border-app-bg"
            />
          ) : (
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center -mt-8 border-2 border-app-bg">
              <span className="text-xl">👥</span>
            </div>
          )}

          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold truncate">{club.name}</h3>
            <p className="text-text-secondary text-xs truncate">
              {club.city}, {club.country}
            </p>
          </div>

          {/* Join Button */}
          {!club.is_member ? (
            <button
              onClick={onJoin}
              className="px-4 py-1.5 rounded-full bg-emerald-500 text-white text-sm font-medium"
            >
              Join
            </button>
          ) : (
            <span className="px-4 py-1.5 rounded-full bg-slate-700 text-text-secondary text-sm">
              Joined
            </span>
          )}
        </div>

        {/* Description */}
        {club.description && (
          <p className="text-text-secondary text-sm mt-3 line-clamp-2">
            {club.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 mt-3 text-xs text-text-secondary">
          <span>👥 {club.members_count} members</span>
          <span>🏃 {club.total_activities} activities</span>
          <span>📏 {club.total_distance_km.toFixed(0)} km</span>
        </div>

        {/* AQI Threshold - Blurp.ie unique */}
        {club.preferred_aqi_threshold && (
          <div className="mt-3 p-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-xs">
              <span>☁️</span>
              <span className="text-cyan-400">
                Max AQI: {club.preferred_aqi_threshold}
              </span>
              <span className="text-text-secondary">
                (No runs above this level)
              </span>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default ClubList;
